import { getToken, clearToken } from '../utils/authToken';
/**
 * SubscriptionWidget - Widget compacto de estado de suscripción
 * Muestra plan actual, límites y uso en el Dashboard
 */

import React, { useState, useEffect, Component } from 'react';
import { Crown, Zap, MessageCircle, Image, ExternalLink, Settings, Loader2, Users, AlertCircle, RefreshCw, X, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';

// Error Boundary para capturar errores de render
class WidgetErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[SubscriptionWidget] ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 rounded-xl border bg-amber-900/20 border-amber-500/30" data-testid="subscription-widget-crash">
          <div className="flex items-center gap-2 text-amber-400 text-sm mb-2">
            <AlertCircle className="w-4 h-4" />
            Error cargando widget
          </div>
          <p className="text-xs text-amber-300/70 mb-2">{this.state.error?.message || 'Error desconocido'}</p>
          <button 
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            className="flex items-center gap-1 text-xs text-cyan-400 hover:underline"
          >
            <RefreshCw className="w-3 h-3" />
            Recargar página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const translations = {
  es: {
    yourPlan: 'Tu Plan',
    free: 'Gratis',
    premium: 'Premium',
    limits: 'Límites',
    usage: 'Uso',
    messagesPerWeek: 'mensajes/semana',
    imagesPerWeek: 'imágenes/semana',
    unlimited: 'Ilimitado',
    used: 'usados',
    remaining: 'restantes',
    bonus: 'bonus',
    upgradeMonthly: 'Premium $9 / mes',
    upgradeYearly: 'Premium $86 / año',
    alreadyPremium: 'Eres Premium',
    manageSubscription: 'Gestionar',
    viewPlans: 'Ver planes',
    viewReferrals: 'Ver referidos',
    loading: 'Cargando...',
    weekResets: 'Se reinicia',
    errorLoading: 'Error cargando suscripción',
    // Cancel / reactivate
    cancelSubscription: 'Cancelar Suscripción',
    cancelTitle: '¿Cancelar tu suscripción Premium?',
    cancelBody: 'Seguirás teniendo acceso hasta el final del período ya pagado.',
    cancelConfirm: 'Confirmar cancelación',
    cancelKeep: 'No, mantener',
    cancelToast: 'Tu suscripción se canceló correctamente. Seguirás siendo Premium hasta el final del período.',
    cancelScheduled: 'Cancelación programada',
    cancelScheduledDesc: 'Tu suscripción se cancelará el',
    reactivate: 'Reactivar suscripción',
    reactivateToast: 'Tu suscripción fue reactivada. Continuará renovándose normalmente.',
    cancelError: 'No se pudo cancelar la suscripción',
    reactivateError: 'No se pudo reactivar la suscripción',
  },
  en: {
    yourPlan: 'Your Plan',
    free: 'Free',
    premium: 'Premium',
    limits: 'Limits',
    usage: 'Usage',
    messagesPerWeek: 'messages/week',
    imagesPerWeek: 'images/week',
    unlimited: 'Unlimited',
    used: 'used',
    remaining: 'remaining',
    bonus: 'bonus',
    upgradeMonthly: 'Premium $9 / mo',
    upgradeYearly: 'Premium $86 / yr',
    alreadyPremium: 'You are Premium',
    manageSubscription: 'Manage',
    viewPlans: 'View plans',
    viewReferrals: 'View referrals',
    loading: 'Loading...',
    weekResets: 'Resets',
    errorLoading: 'Error loading subscription',
    // Cancel / reactivate
    cancelSubscription: 'Cancel Subscription',
    cancelTitle: 'Cancel your Premium subscription?',
    cancelBody: 'You will still have access until the end of your current billing period.',
    cancelConfirm: 'Confirm cancellation',
    cancelKeep: 'No, keep it',
    cancelToast: 'Your subscription was canceled. You will remain Premium until the end of the period.',
    cancelScheduled: 'Cancellation scheduled',
    cancelScheduledDesc: 'Your subscription will end on',
    reactivate: 'Reactivate subscription',
    reactivateToast: 'Your subscription was reactivated. It will continue renewing normally.',
    cancelError: 'Could not cancel the subscription',
    reactivateError: 'Could not reactivate the subscription',
  }
};

const SubscriptionWidget = ({ compact = false }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language] || translations.es;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoadingAction, setIsLoadingAction] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Permite recargar el estado tras cancelar / reactivar sin esperar a Stripe.
  const reloadSubscription = async () => {
    const token = getToken();
    if (!token || token === 'guest') return;
    try {
      const response = await axios.get(`${API_URL}/subscriptions/status`, {
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      // mantener data previa, solo loggear
      console.warn('reloadSubscription error', err?.message);
    }
  };
  
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      const token = getToken();
      
      if (!token || token === 'guest') {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/subscriptions/status`, {
        });
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };
    
    loadSubscriptionStatus();
  }, []);
  
  const handleUpgrade = async (period) => {
    const token = getToken();
    if (!token) {
      window.location.href = '/login';
      return;
    }
    
    setIsLoadingAction(period);
    
    try {
      const response = await axios.post(
        `${API_URL}/subscriptions/checkout`,
        {
          period,
          origin_url: window.location.origin
        },
        {}
      );
      
      if (response.data.checkout_url) {
        const stripeWindow = window.open(response.data.checkout_url, '_blank');
        if (!stripeWindow) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : t.errorLoading;
      toast.error(errorMsg);
    } finally {
      setIsLoadingAction(null);
    }
  };
  
  const handleManageSubscription = async () => {
    const token = getToken();
    if (!token) return;
    
    setIsLoadingAction(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/subscriptions/billing-portal`,
        {},
        {}
      );
      
      if (response.data.url) {
        // Stripe Billing Portal envia X-Frame-Options: DENY — abrir en
        // pestana nueva para que SIEMPRE funcione (preview, PWA, mobile).
        const w = window.open(response.data.url, '_blank', 'noopener,noreferrer');
        if (!w) {
          try { window.top.location.href = response.data.url; }
          catch (_e) { window.location.href = response.data.url; }
        }
        setIsLoadingAction(null);
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error(error.response?.data?.detail || t.errorLoading);
      setIsLoadingAction(false);
    }
  };

  // Confirmar cancelacion (POST /api/subscriptions/cancel — llama Stripe)
  const handleCancelConfirm = async () => {
    const token = getToken();
    if (!token) return;
    setIsLoadingAction('cancel');
    try {
      await axios.post(
        `${API_URL}/subscriptions/cancel`,
        {},
        {}
      );
      toast.success(t.cancelToast, { duration: 7000 });
      setShowCancelModal(false);
      await reloadSubscription();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t.cancelError);
    } finally {
      setIsLoadingAction(null);
    }
  };

  // Reactivar (POST /api/subscriptions/reactivate)
  const handleReactivate = async () => {
    const token = getToken();
    if (!token) return;
    setIsLoadingAction('reactivate');
    try {
      await axios.post(
        `${API_URL}/subscriptions/reactivate`,
        {},
        {}
      );
      toast.success(t.reactivateToast, { duration: 6000 });
      await reloadSubscription();
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t.reactivateError);
    } finally {
      setIsLoadingAction(null);
    }
  };
  
  if (loading) {
    return (
      <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50" data-testid="subscription-widget-loading">
        <div className="flex items-center gap-2 text-slate-400 text-xs">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t.loading}
        </div>
      </div>
    );
  }
  
  // Si hay error, mostrar mensaje de error en vez de nada
  if (error) {
    return (
      <div className="p-4 rounded-xl border bg-red-900/20 border-red-500/30" data-testid="subscription-widget-error">
        <div className="flex items-center gap-2 text-red-400 text-sm mb-2">
          <Zap className="w-4 h-4" />
          {t.errorLoading}
        </div>
        <p className="text-xs text-red-300/70">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 text-xs text-cyan-400 hover:underline"
        >
          {language === 'es' ? 'Reintentar' : 'Retry'}
        </button>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="p-4 rounded-xl border bg-slate-800/50 border-slate-700/50" data-testid="subscription-widget-nodata">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-slate-700/50">
              <Zap className="w-4 h-4 text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t.yourPlan}</p>
              <p className="text-sm font-bold text-slate-300">{t.free}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/planes-y-beneficios')}
            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          >
            {t.viewPlans}
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
        
        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-400 flex items-center gap-1.5">
              <MessageCircle className="w-3 h-3" />
              {t.messagesPerWeek}
            </span>
            <span className="text-slate-300">0 / 20</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-400 flex items-center gap-1.5">
              <Image className="w-3 h-3" />
              {t.imagesPerWeek}
            </span>
            <span className="text-slate-300">0 / 4</span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => handleUpgrade('monthly')}
            disabled={isLoadingAction !== null}
            className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:from-purple-400 hover:to-pink-400 transition-all flex items-center justify-center gap-2"
            data-testid="upgrade-monthly-btn"
          >
            {isLoadingAction === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
            $9
          </button>
          <button
            onClick={() => handleUpgrade('yearly')}
            disabled={isLoadingAction !== null}
            className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-lg hover:from-purple-400 hover:to-pink-400 transition-all flex items-center justify-center gap-2"
            data-testid="upgrade-yearly-btn"
          >
            {isLoadingAction === 'yearly' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
            $86
          </button>
        </div>
      </div>
    );
  }
  
  const isPremium = data.is_premium;
  const usage = data.usage || {};
  
  // Formatear fecha de reinicio
  const formatResetDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  
  if (compact) {
    // Versión compacta para el header
    return (
      <div className="flex items-center gap-2">
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          isPremium 
            ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30' 
            : 'bg-slate-700/50 text-slate-300 border border-slate-600/50'
        }`}>
          {isPremium ? <Crown className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
          {isPremium ? t.premium : t.free}
        </div>
        
        {!isPremium && (
          <div className="flex gap-1">
            <button
              onClick={() => handleUpgrade('monthly')}
              disabled={isLoadingAction !== null}
              className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full hover:from-purple-400 hover:to-pink-400 transition-all"
            >
              $9
            </button>
            <button
              onClick={() => handleUpgrade('yearly')}
              disabled={isLoadingAction !== null}
              className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full hover:from-purple-400 hover:to-pink-400 transition-all"
            >
              $86
            </button>
          </div>
        )}
      </div>
    );
  }
  
  // Versión completa para el Dashboard
  return (
    <div 
      data-testid="subscription-widget-full"
      className={`p-4 rounded-xl border ${
      isPremium 
        ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/20 border-purple-500/40 shadow-lg shadow-purple-500/10' 
        : 'bg-slate-800/50 border-slate-700/50'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${
            isPremium 
              ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
              : 'bg-slate-700'
          }`}>
            {isPremium ? <Crown className="w-4 h-4 text-white" /> : <Zap className="w-4 h-4 text-slate-300" />}
          </div>
          <div>
            <span className="text-xs text-slate-400">{t.yourPlan}</span>
            <p className={`font-semibold flex items-center gap-1 ${isPremium ? 'text-purple-300' : 'text-white'}`}>
              {isPremium ? t.premium : t.free}
              {isPremium && <span className="text-lg">👑</span>}
            </p>
          </div>
        </div>
        
        <button
          type="button"
          onClick={() => navigate('/planes-y-beneficios')}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
        >
          {t.viewPlans}
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>
      
      {/* Link a Referidos con badge de bonus */}
      <button
        type="button"
        onClick={() => navigate('/referidos')}
        className="w-full flex items-center gap-2 mb-4 px-3 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors group relative text-left"
        data-testid="view-referrals-link"
      >
        <Users className="w-4 h-4 text-purple-400" />
        <span className="text-sm text-slate-300 group-hover:text-white transition-colors">{t.viewReferrals}</span>
        {usage.bonus_messages_available > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-bold animate-pulse">
            +{usage.bonus_messages_available}
          </span>
        )}
        <ExternalLink className="w-3 h-3 text-slate-500 ml-auto" />
      </button>
      
      {/* Uso */}
      {!isPremium && (
        <div className="space-y-3 mb-4">
          {/* Mensajes */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-400 flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />
                {t.messagesPerWeek}
              </span>
              <span className="text-slate-300">
                {usage.messages_used || 0} / {usage.messages_limit === -1 ? '∞' : usage.messages_limit}
                {usage.bonus_messages_available > 0 && (
                  <span className="text-emerald-400 ml-1">+{usage.bonus_messages_available} {t.bonus}</span>
                )}
              </span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
                style={{ 
                  width: usage.messages_limit === -1 
                    ? '0%' 
                    : `${Math.min(100, (usage.messages_used / usage.messages_limit) * 100)}%` 
                }}
              />
            </div>
          </div>
          
          {/* Imágenes */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-400 flex items-center gap-1">
                <Image className="w-3 h-3" />
                {t.imagesPerWeek}
              </span>
              <span className="text-slate-300">
                {usage.images_used || 0} / {usage.images_limit === -1 ? '∞' : usage.images_limit}
              </span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                style={{ 
                  width: usage.images_limit === -1 
                    ? '0%' 
                    : `${Math.min(100, (usage.images_used / usage.images_limit) * 100)}%` 
                }}
              />
            </div>
          </div>
          
          {/* Fecha de reinicio */}
          {usage.week_resets_at && (
            <p className="text-xs text-slate-500">
              {t.weekResets}: {formatResetDate(usage.week_resets_at)}
            </p>
          )}
        </div>
      )}
      
      {/* Premium status */}
      {isPremium && (
        <div className="mb-4">
          <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20 mb-3">
            <p className="text-sm text-purple-200 mb-2">
              {language === 'es' ? '💜 Gracias por apoyar DamyxIA' : '💜 Thanks for supporting DamyxIA'}
            </p>
            <div className="flex items-center gap-2 text-sm text-purple-300">
              <MessageCircle className="w-4 h-4" />
              <span>{t.unlimited}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-purple-300 mt-1">
              <Image className="w-4 h-4" />
              <span>{t.unlimited}</span>
            </div>
          </div>

          {/* Cancelacion programada — bloque visible solo si Stripe marco
              cancel_at_period_end=true. Muestra fecha exacta y CTA Reactivar. */}
          {data.cancel_at_period_end && (
            <div
              className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-3 flex items-start gap-2"
              data-testid="subscription-cancel-scheduled"
            >
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-xs text-amber-200">
                <p className="font-semibold">{t.cancelScheduled}</p>
                <p className="text-amber-300/80 mt-0.5">
                  {t.cancelScheduledDesc}{' '}
                  {data.cancel_at
                    ? new Date(data.cancel_at).toLocaleDateString(
                        language === 'es' ? 'es-ES' : 'en-US',
                        { day: '2-digit', month: 'long', year: 'numeric' }
                      )
                    : '—'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex flex-col gap-2">
        {isPremium ? (
          <>
            <div className="flex gap-2">
              <button
                disabled
                className="flex-1 py-2 px-3 bg-emerald-600/20 text-emerald-400 rounded-lg text-sm font-medium flex items-center justify-center gap-2 cursor-default"
              >
                <Crown className="w-4 h-4" />
                {t.alreadyPremium}
              </button>
              <button
                onClick={handleManageSubscription}
                disabled={isLoadingAction !== null}
                className="py-2 px-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                data-testid="manage-subscription-btn"
              >
                {isLoadingAction === true ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Settings className="w-4 h-4" />
                )}
                {t.manageSubscription}
              </button>
            </div>

            {/* Cancelar / Reactivar — solo si la suscripcion esta vinculada a
                Stripe (stripe_subscription_id presente). Cuentas tipo
                premium_permanent / creator no muestran estos botones. */}
            {data.stripe_subscription_id && (
              data.cancel_at_period_end ? (
                <button
                  onClick={handleReactivate}
                  disabled={isLoadingAction !== null}
                  className="py-2 px-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                  data-testid="reactivate-subscription-btn"
                >
                  {isLoadingAction === 'reactivate' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  {t.reactivate}
                </button>
              ) : (
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={isLoadingAction !== null}
                  className="py-2 px-3 bg-transparent hover:bg-red-500/10 border border-red-500/40 text-red-400 hover:text-red-300 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                  data-testid="cancel-subscription-btn"
                >
                  <X className="w-4 h-4" />
                  {t.cancelSubscription}
                </button>
              )
            )}
          </>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => handleUpgrade('monthly')}
              disabled={isLoadingAction !== null}
              className="flex-1 py-2 px-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="widget-upgrade-monthly-btn"
            >
              {isLoadingAction === 'monthly' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Crown className="w-4 h-4" />
              )}
              $9
            </button>
            <button
              onClick={() => handleUpgrade('yearly')}
              disabled={isLoadingAction !== null}
              className="flex-1 py-2 px-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="widget-upgrade-yearly-btn"
            >
              {isLoadingAction === 'yearly' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Crown className="w-4 h-4" />
              )}
              $86
            </button>
          </div>
        )}
      </div>

      {/* Modal de confirmacion de cancelacion */}
      {showCancelModal && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => isLoadingAction !== 'cancel' && setShowCancelModal(false)}
          data-testid="cancel-subscription-modal"
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-white mb-2" data-testid="cancel-modal-title">
              {t.cancelTitle}
            </h3>
            <p className="text-sm text-zinc-400 mb-5">{t.cancelBody}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={isLoadingAction === 'cancel'}
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="cancel-modal-keep-btn"
              >
                {t.cancelKeep}
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={isLoadingAction === 'cancel'}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60"
                data-testid="cancel-modal-confirm-btn"
              >
                {isLoadingAction === 'cancel' && <Loader2 className="w-4 h-4 animate-spin" />}
                {t.cancelConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper con ErrorBoundary para garantizar que siempre se muestre algo
const SubscriptionWidgetWithErrorBoundary = (props) => (
  <WidgetErrorBoundary>
    <SubscriptionWidget {...props} />
  </WidgetErrorBoundary>
);

export default SubscriptionWidgetWithErrorBoundary;
